import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { TrendingUp, ChevronRight, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getPopularMovies, getPopularTVShows, getRecentGameReleases } from '../../services/api';

const PanelContainer = styled.aside`
  width: 320px;
  background: ${({ theme }) => theme.isDarkMode ? '#101113' : '#FBFBFB'};
  border-left: 1px solid ${({ theme }) => theme.colors.border};
  height: calc(100vh - 64px);
  position: fixed;
  right: var(--vk-offset, 0px);
  top: 64px;
  padding: 20px;
  overflow-y: auto;
  z-index: 100;
  transition: background-color 0.3s ease, border-color 0.3s ease;

  &::-webkit-scrollbar {
    display: none;
  }
  
  -ms-overflow-style: none;
  scrollbar-width: none;

  @media (max-width: 1200px) {
    width: 280px;
  }

  @media (max-width: 1024px) {
    display: none;
  }
`;

const Section = styled.section`
  margin-bottom: 24px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const SeeAllLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const RecommendationCard = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 12px;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const RecommendationImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const RecommendationOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.3) 0%,
    rgba(0, 0, 0, 0.1) 40%,
    rgba(0, 0, 0, 0.7) 100%
  );
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 12px;
`;

const TopSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const CategoryBadge = styled.span`
  background: ${({ theme }) => theme.isDarkMode ? '#EFF3F7' : '#18191B'};
  color: ${({ theme }) => theme.isDarkMode ? '#18191B' : '#FFFFFF'};
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
`;

const RatingBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(0, 0, 0, 0.6);
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
`;

const RatingIcon = styled.img`
  width: 16px;
  height: 16px;
  filter: brightness(0) saturate(100%) invert(100%);
`;

const RatingValue = styled.span`
  color: white;
`;

const BottomSection = styled.div`
  color: white;
  text-align: center;
`;

const RecommendationTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: white;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-align: center;
`;

const RecommendationYear = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 700;
  display: block;
  margin-bottom: 4px;
  text-align: center;
`;

const RecommendationOverview = styled.p`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.85);
  font-style: italic;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-align: center;
`;

const Rating = styled.span`
  display: flex;
  align-items: center;
  gap: 2px;
  color: #f59e0b;
`;

const RatingsContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const AdCard = styled.div`
  background: ${({ theme }) => theme.colors.surfaceHover};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  transition: background-color 0.3s ease;
`;

const AdLabel = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : '#18191B'};
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const AdTitle = styled.h4`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 8px 0 4px;
`;

const AdDescription = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.4;
  white-space: pre-wrap;
  margin: 0;

  strong {
    font-weight: 700;
    color: ${({ theme }) => theme.colors.text};
  }

  em {
    font-style: italic;
  }

  u {
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

const AdButton = styled.button`
  background: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : '#18191B'};
  color: ${({ theme }) => theme.isDarkMode ? '#18191B' : '#EFF3F7'};
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  margin-top: 12px;
  transition: background 0.2s;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.isDarkMode ? '#EFF3F7' : '#2a2d30'};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: ${({ theme }) => theme.colors.textSecondary};
  gap: 8px;
`;

const CategoryLabel = styled.span`
  font-size: 11px;
  color: #65676b;
  margin-left: auto;
`;

const API_URL = '';

const escapeHtml = (value = '') =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const formatAdDescription = (rawText = '') => {
  if (!rawText) return '';
  let text = escapeHtml(rawText);

  // Поддержка простого markdown: **bold**, *italic*, __underline__
  text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  text = text.replace(/\*(.+?)\*/g, '<em>$1</em>');
  text = text.replace(/__(.+?)__/g, '<u>$1</u>');

  return text;
};

const RightPanel = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ad, setAd] = useState(null);

  // Загрузка случайного рекламного баннера
  useEffect(() => {
    const loadAd = async () => {
      try {
        const res = await fetch(`${API_URL}/api/ads/random`);
        if (res.ok) {
          const data = await res.json();
          setAd(data);
        }
      } catch {
        // игнорируем — если нет рекламы, показываем статичную
      }
    };
    loadAd();
  }, []);

  // Регистрация клика
  const handleAdClick = async (adItem) => {
    try {
      const token = localStorage.getItem('polka_token');
      let userPayload = {};
      if (token) {
        try {
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          userPayload = JSON.parse(window.atob(base64));
        } catch {
          // ignore
        }
      }
      await fetch(`${API_URL}/api/ads/${adItem.id}/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userPayload.id || null,
          user_email: userPayload.email || ''
        })
      });
      if (adItem.button_link) {
        window.open(adItem.button_link, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('Click error:', err);
    }
  };

  useEffect(() => {
    const loadRecommendations = async () => {
      try {
        setLoading(true);
        const [movies, series, games] = await Promise.all([
          getPopularMovies(2),
          getPopularTVShows(2),
          getRecentGameReleases(2)
        ]);

        const allRecommendations = [
          ...movies.map(item => ({ ...item, type: 'Фильм' })),
          ...series.map(item => ({ ...item, type: 'Сериал' })),
          ...games.map(item => ({ ...item, type: 'Игра' }))
        ];

        setRecommendations(allRecommendations);
      } catch (error) {
        console.error('Error loading recommendations:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRecommendations();
  }, []);

  return (
    <PanelContainer>
      {/* Рекламный блок */}
      {ad ? (
        <AdCard key={ad.id} onClick={() => handleAdClick(ad)} style={{ cursor: ad.button_link ? 'pointer' : 'default' }}>
          <AdLabel>{ad.label || t('common.adLabel')}</AdLabel>
          {ad.type === 'image' && ad.image_path && (
            <img
              src={`${API_URL}${ad.image_path}`}
              alt={ad.title || ''}
              style={{ width: '100%', borderRadius: 8, marginBottom: ad.title || ad.description || ad.button_text ? 8 : 0 }}
            />
          )}
          {ad.title && <AdTitle>{ad.title}</AdTitle>}
          {ad.description && (
            <AdDescription
              dangerouslySetInnerHTML={{ __html: formatAdDescription(ad.description) }}
            />
          )}
          {ad.button_text && <AdButton onClick={(e) => { e.stopPropagation(); handleAdClick(ad); }}>{ad.button_text}</AdButton>}
        </AdCard>
      ) : (
        <AdCard>
          <AdLabel>{t('common.whatsNew')}</AdLabel>
          <AdTitle>ПОЛКА в сети!</AdTitle>
          <AdDescription>
            УРА! Это действительно произошло. Спасибо вам, что верили в Полку и ждали.
          </AdDescription>
          <AdButton>{t('common.great')}</AdButton>
        </AdCard>
      )}

      {/* Рекомендации */}
      <Section>
        <SectionHeader>
          <SectionTitle>
            <TrendingUp size={18} />
            {t('common.recommendations')}
          </SectionTitle>
        </SectionHeader>

        {loading ? (
          <LoadingContainer>
            <Loader size={16} className="spinning" />
            {t('common.loadingRecommendations')}
          </LoadingContainer>
        ) : (
          recommendations.map(item => (
            <RecommendationCard
              key={`${item.type}-${item.id}`}
              onClick={() => {
                const category = item.category || (item.type === 'Фильм' ? 'film' : item.type === 'Сериал' ? 'series' : item.type === 'Игра' ? 'game' : 'movie');
                navigate(`/item/${category}/${item.id}`, {
                  state: { item }
                });
              }}
            >
              <RecommendationImage 
                src={item.backdrop || item.background || item.screenshots?.[0]?.image || item.poster || 'https://via.placeholder.com/300x180?text=No+Image'} 
                alt={item.title} 
              />
              <RecommendationOverlay>
                <TopSection>
                  <CategoryBadge>{item.type}</CategoryBadge>
                  <RatingsContainer>
                    {item.type === 'Игра' ? (
                      item.metacritic > 0 && (
                        <RatingBadge>
                          <RatingIcon src="/assets/metacritic.svg" alt="Metacritic" />
                          <RatingValue>{item.metacritic}</RatingValue>
                        </RatingBadge>
                      )
                    ) : item.rating > 0 ? (
                      <RatingBadge>
                        <RatingIcon src="/assets/tmdbicon.svg" alt="TMDB" />
                        <RatingValue>{item.rating.toFixed(1)}</RatingValue>
                      </RatingBadge>
                    ) : null}
                  </RatingsContainer>
                </TopSection>
                <BottomSection>
                  <RecommendationTitle>{item.title}</RecommendationTitle>
                  {item.releaseDate && (
                    <RecommendationYear>
                      {new Date(item.releaseDate).getFullYear()}
                    </RecommendationYear>
                  )}
                  {item.overview && (
                    <RecommendationOverview>
                      {item.overview.length > 150 
                        ? `${item.overview.substring(0, 150)}...` 
                        : item.overview}
                    </RecommendationOverview>
                  )}
                </BottomSection>
              </RecommendationOverlay>
            </RecommendationCard>
          ))
        )}
      </Section>

    </PanelContainer>
  );
};

export default RightPanel;
